import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { SSRConfig, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Error from 'next/error';
import Head from 'next/head';

import { isAxiosError } from '@/apis/aniftyBackend/client';
import fetchUserByID from '@/apis/aniftyBackend/users/fetchUserByID';
import { User } from '@/apis/aniftyBackend/users/interface';
import UserProfilePage from '@/containers/UserProfilePage';
import { captureException } from '@/utils/Sentry';
import getDisplayName from '@/utils/getDisplayName';
import { convertImgixURL } from '@/utils/imgixUtils';

type Props = SSRConfig & {
  user: User | null;
  errorCode: number | null;
};

const UserProfile: NextPage<Props> = ({ user, errorCode }: Props) => {
  const { i18n } = useTranslation();

  if (errorCode || !user) {
    return <Error statusCode={errorCode || 500} />;
  }

  const displayName = getDisplayName(user, i18n.language);

  const ogpImageURL = convertImgixURL(user.profilePicUrl, {
    w: 1200,
  });

  return (
    <>
      <Head>
        <title>{displayName} | ANIFTY</title>
        <meta property="description" key="description" content={user.about} />
        <meta property="og:title" key="og:title" content={displayName} />
        <meta
          property="og:description"
          key="og:description"
          content={user.about}
        />
        <meta property="og:image" key="og:image" content={ogpImageURL} />
        <meta
          name="twitter:card"
          key="twitter:card"
          content="summary_large_image"
        />
      </Head>
      <UserProfilePage user={user} />
    </>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({
  locale,
  params,
}) => {
  const translations = await serverSideTranslations(locale, [
    'common',
    'limitedCollection',
  ]);

  if (!params || typeof params.userName !== 'string') {
    // username is not specified
    return {
      notFound: true,
    };
  }

  if (!/^@\w{1,16}$/.test(params.userName)) {
    // username is not valid
    return {
      notFound: true,
    };
  }

  // fetch user
  let errorCode: null | number = null;
  let user: User | null = null;

  try {
    user = await fetchUserByID(params.userName);
  } catch (err) {
    if (
      isAxiosError(err) &&
      err.response &&
      err.response.status >= 400 &&
      err.response.status < 500
    ) {
      // user not found or invalid argument
      return {
        notFound: true,
      };
    }

    // unknown error -> treat as internal error
    errorCode = 500;
    captureException(err);
  }

  if (user && user.typeID === 3) {
    // contract does not have user page
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...translations,
      user,
      errorCode,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default UserProfile;
